import { useQuery } from "@tanstack/react-query";
import { useState, type CSSProperties } from "react";
import { api } from "../../lib/api";
import { backdropClose } from "../shared/backdrop";
import { ConversationModal, fmtPhone } from "../shared/ConversationModal";
import { DialogPanel } from "../shared/DialogPanel";

interface CostSlice {
  label: string;
  tokens_in: number;
  tokens_out: number;
  tokens_cached: number;
  tokens_thinking: number;
  tokens_total: number;
  cost_usd: number;
  no_price?: boolean; // consumo de modelo sem preço em Dev → Modelos
}

interface Metrics {
  period: string;
  messages_processed: number;
  unique_customers: number;
  qualified_human: number;
  payg_messages: number;
  payg_leads_total: number;
  executions_total: number;
  executions_errors: number;
  error_rate: number;
  tokens_in: number;
  tokens_out: number;
  tokens_thinking: number;
  tokens_cached: number;
  tokens_total: number;
  cost_total_usd: number;
  cost_breakdown: CostSlice[];
  conversations_with_cost: number;
  avg_cost_per_conversation_usd: number;
}

interface ConvCost {
  conversation_id: string;
  cost_usd: number;
  external_user_id: string;
  bsuid: string | null;
  lead_username: string | null;
  created_at: string | null;
  last_message_at: string | null;
}

const PERIODS = [
  { value: "today",     label: "Hoje" },
  { value: "yesterday", label: "Ontem" },
  { value: "7",         label: "7 dias" },
  { value: "15",        label: "15 dias" },
  { value: "30",        label: "30 dias" },
  { value: "60",        label: "60 dias" },
  { value: "90",        label: "90 dias" },
];

function fmtNumber(n: number) {
  return n.toLocaleString("pt-BR");
}

function fmtSliceLabel(label: string) {
  // A API manda uso de tools como "tool:nome_da_tool" — exibe amigável.
  return label.startsWith("tool:") ? `Ferramenta: ${label.slice(5)}` : label;
}

function fmtUsd(n: number) {
  // Chamadas de LLM baratas custam fração de centavo — mostra mais casas
  // quando o valor é pequeno para não exibir tudo como $0.00.
  const maxDecimals = n > 0 && n < 0.01 ? 6 : 2;
  return "$" + n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: maxDecimals });
}

const BAR_COLORS = ["var(--brand)", "#1FA2DD", "var(--violet)", "#0F9D63", "var(--warn)", "#0891B2", "var(--violet)", "var(--ok)", "var(--danger)", "var(--info)"];

function CostBar({ slices }: { slices: CostSlice[] }) {
  const totalTokens = slices.reduce((s, x) => s + x.tokens_total, 0);
  const totalCost = slices.reduce((s, x) => s + x.cost_usd, 0);
  if (totalTokens === 0) return <div style={{ color: "var(--ink-muted)", fontSize: 13 }}>Sem consumo de tokens no período.</div>;

  return (
    <div>
      {/* Header: total de tokens */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: "var(--ink-secondary)" }}>Total de tokens</span>
        <span style={{ fontSize: 26, fontWeight: 700, color: "var(--ink)" }}>{fmtNumber(totalTokens)}</span>
      </div>

      {/* Barra empilhada por custo */}
      {totalCost > 0 ? (
        <div style={{ display: "flex", width: "100%", height: 22, borderRadius: 6, overflow: "hidden", gap: 1 }}>
          {slices.map((s, i) => {
            const pct = (s.cost_usd / totalCost) * 100;
            if (pct <= 0) return null;
            return (
              <div
                key={s.label}
                title={`${fmtSliceLabel(s.label)}: ${fmtUsd(s.cost_usd)}`}
                style={{ width: `${pct}%`, minWidth: 2, background: BAR_COLORS[i % BAR_COLORS.length] }}
              />
            );
          })}
        </div>
      ) : (
        <div style={{ fontSize: 12, color: "var(--ink-muted)" }}>Nenhum modelo com preço cadastrado no período.</div>
      )}

      {/* Cabeçalho das colunas */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 16, paddingBottom: 6, borderBottom: "1px solid var(--line)" }}>
        <span style={{ fontSize: 11, color: "var(--ink-muted)", textTransform: "uppercase", letterSpacing: ".04em" }}>Modelo</span>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <span style={{ fontSize: 11, color: "var(--ink-muted)", textTransform: "uppercase", letterSpacing: ".04em", minWidth: 80, textAlign: "right" }}>Custo</span>
          <span style={{ fontSize: 11, color: "var(--ink-muted)", textTransform: "uppercase", letterSpacing: ".04em", minWidth: 72, textAlign: "right" }}>Entrada</span>
          <span style={{ fontSize: 11, color: "var(--ink-muted)", textTransform: "uppercase", letterSpacing: ".04em", minWidth: 72, textAlign: "right" }}>Saída</span>
        </div>
      </div>

      {/* Lista de modelos (ordenada por custo) */}
      <div style={{ display: "flex", flexDirection: "column" }}>
        {slices.map((s, i) => (
          <div key={s.label} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "9px 0", borderBottom: "1px solid var(--surface-input)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
              <span style={{ width: 11, height: 11, borderRadius: 3, background: BAR_COLORS[i % BAR_COLORS.length] }} />
              <span style={{ fontSize: 13, color: "var(--ink)" }}>{fmtSliceLabel(s.label)}</span>
              {/* Consumo com preço zerado = modelo sem preço em Dev → Modelos (custo real fica fora). */}
              {s.no_price && (
                <span
                  title="Modelo sem preço cadastrado em Dev → Modelos: o consumo existe, mas o custo não entra no total."
                  style={{ fontSize: 11, color: "var(--warn)", background: "var(--warn-soft)", border: "1px solid var(--warn-line)", borderRadius: 4, padding: "1px 6px" }}
                >sem preço no catálogo</span>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <span style={{ fontSize: 13, color: "var(--ink-secondary)", minWidth: 80, textAlign: "right" }}>{fmtUsd(s.cost_usd)}</span>
              {/* Entrada = input + cache; Saída = resposta + thinking. O title abre a divisão no hover. */}
              <span
                title={`Input: ${fmtNumber(Math.max(s.tokens_in - (s.tokens_cached ?? 0), 0))} tk\nCache: ${fmtNumber(s.tokens_cached ?? 0)} tk`}
                style={{ fontSize: 13, color: "var(--ink-secondary)", minWidth: 72, textAlign: "right", cursor: "help" }}
              >{fmtNumber(s.tokens_in)}</span>
              <span
                title={`Saída: ${fmtNumber(Math.max(s.tokens_out - (s.tokens_thinking ?? 0), 0))} tk\nThinking: ${fmtNumber(s.tokens_thinking ?? 0)} tk`}
                style={{ fontSize: 14, fontWeight: 600, color: "var(--ink)", minWidth: 72, textAlign: "right", cursor: "help" }}
              >{fmtNumber(s.tokens_out)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Simulação pay-as-you-go ────────────────────────────────────────────────
// Calculadora só em memória (useState): nada é salvo, F5 volta ao padrão.
interface PaygRow { msg: string; qual: string; sobr: string; preco: string; pct: string }
const PAYG_DEFAULT: PaygRow = { msg: "1", qual: "20", sobr: "10", preco: "0,17", pct: "" };
const PAYG_BLANK: PaygRow = { msg: "", qual: "", sobr: "", preco: "", pct: "" };
const PAYG_FIELDS: { key: keyof PaygRow; label: string }[] = [
  { key: "msg", label: "Créd / mensagem" },
  { key: "qual", label: "Créd / qualificado" },
  { key: "sobr", label: "Créd / sobressalente" },
  { key: "preco", label: "R$ / crédito" },
  { key: "pct", label: "% qualificados" },
];

function parseNum(s: string): number | null {
  const t = s.trim().replace(",", ".");
  const n = Number(t);
  return t && Number.isFinite(n) && n >= 0 ? n : null;
}

// % preenchido → fatia do total vira "qualificados" só nesta linha; vazio → métrica real.
function paygCalc(row: PaygRow, msgs: number, leadsTotal: number, qualReal: number) {
  const a = parseNum(row.msg), b = parseNum(row.qual), c = parseNum(row.sobr), p = parseNum(row.preco);
  if (a === null || b === null || c === null || p === null) return null;
  const pct = parseNum(row.pct);
  const qual = pct === null ? qualReal : Math.round(leadsTotal * Math.min(pct, 100) / 100);
  const credits = msgs * a + qual * b + Math.max(leadsTotal - qual, 0) * c;
  return { credits, brl: credits * p };
}

const PAYG_HEAD: CSSProperties = { fontSize: 11, color: "var(--ink-muted)", textTransform: "uppercase", letterSpacing: ".04em" };

function PaygSim({ data }: { data: Metrics }) {
  const [rows, setRows] = useState<PaygRow[]>([PAYG_DEFAULT, PAYG_BLANK, PAYG_BLANK, PAYG_BLANK]);
  const msgs = data.payg_messages ?? 0;
  const leadsTotal = data.payg_leads_total ?? 0;
  const qualReal = data.qualified_human ?? 0;
  const kpis = [
    { label: "Mensagens enviadas", value: msgs, sub: "respostas + follow-ups (1 por envio)" },
    { label: "Leads qualificados", value: qualReal, sub: undefined },
    { label: "Leads sobressalentes", value: Math.max(leadsTotal - qualReal, 0), sub: `${fmtNumber(leadsTotal)} atendidos − qualificados` },
  ];

  return (
    <div style={{ background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 10, padding: "18px 20px" }}>
      <div style={{ ...PAYG_HEAD, marginBottom: 14 }}>Simulação pay as you go</div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12, marginBottom: 20 }}>
        {kpis.map(k => (
          <div key={k.label} className="metric-card" style={{ background: "var(--surface-card)", border: "1px solid var(--line)", borderRadius: 10, padding: "16px 18px" }}>
            <div style={{ ...PAYG_HEAD, marginBottom: 10 }}>{k.label}</div>
            <div className="metric-value" style={{ fontSize: 26, fontWeight: 700, lineHeight: 1 }}>{fmtNumber(k.value)}</div>
            {k.sub && <div style={{ fontSize: 11, color: "var(--ink-dim)", marginTop: 6 }}>{k.sub}</div>}
          </div>
        ))}
      </div>

      <div style={{ overflowX: "auto" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(96px, 1fr)) minmax(90px, auto) minmax(120px, auto)", gap: "8px 10px", alignItems: "center", minWidth: 760 }}>
          {PAYG_FIELDS.map(f => <span key={f.key} style={PAYG_HEAD}>{f.label}</span>)}
          <span style={{ ...PAYG_HEAD, textAlign: "right" }}>Créditos</span>
          <span style={{ ...PAYG_HEAD, textAlign: "right" }}>Total</span>
          {rows.map((row, i) => {
            const r = paygCalc(row, msgs, leadsTotal, qualReal);
            return [
              ...PAYG_FIELDS.map(f => (
                <input
                  key={`${i}-${f.key}`}
                  inputMode="decimal"
                  aria-label={`${f.label} — linha ${i + 1}`}
                  placeholder={f.key === "pct" ? "real" : ""}
                  value={row[f.key]}
                  onChange={e => setRows(rs => rs.map((x, j) => (j === i ? { ...x, [f.key]: e.target.value } : x)))}
                  style={{ width: "100%", boxSizing: "border-box", background: "var(--surface-card)", border: "1px solid var(--line-strong)", borderRadius: 6, padding: "7px 9px", fontSize: 13, color: "var(--ink)" }}
                />
              )),
              <span key={`${i}-cred`} style={{ fontSize: 13, color: "var(--ink-secondary)", textAlign: "right" }}>
                {r ? fmtNumber(r.credits) : "—"}
              </span>,
              <span key={`${i}-brl`} style={{ fontSize: 16, fontWeight: 700, color: r ? "var(--brand)" : "var(--ink-dim)", textAlign: "right" }}>
                {r ? r.brl.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) : "—"}
              </span>,
            ];
          })}
        </div>
      </div>
    </div>
  );
}

interface Card {
  label: string;
  value: string;
  sub?: string;
  color?: string;
  tooltip?: string;  // divisão do valor no hover (title nativo)
  onClick?: () => void;
}

export function TabMetricas({ agent }: { agent: { id: string } }) {
  const [period, setPeriod] = useState("7");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showConvCosts, setShowConvCosts] = useState(false);
  const [convSel, setConvSel] = useState<ConvCost | null>(null);
  const rangeActive = !!(startDate && endDate);

  const query = rangeActive
    ? `start_date=${startDate}&end_date=${endDate}`
    : `period=${period}`;

  const { data, isLoading } = useQuery<Metrics>({
    queryKey: ["agent-metrics", agent.id, rangeActive ? `${startDate}_${endDate}` : period],
    queryFn: () => api.get(`/agents/${agent.id}/metrics?${query}`),
  });

  // Ranking da popup — só busca com ela aberta; mesma janela do dash.
  const { data: convCosts, isLoading: convCostsLoading } = useQuery<ConvCost[]>({
    queryKey: ["agent-conv-costs", agent.id, rangeActive ? `${startDate}_${endDate}` : period],
    queryFn: () => api.get(`/agents/${agent.id}/metrics/conversation-costs?${query}`),
    enabled: showConvCosts,
  });

  function selectPeriod(p: string) {
    setPeriod(p);
    setStartDate("");
    setEndDate("");
  }

  const cards: Card[] = [
    {
      label: "Execuções totais",
      value: data ? fmtNumber(data.executions_total) : "—",
    },
    {
      label: "Erros",
      value: data ? fmtNumber(data.executions_errors) : "—",
      color: data && data.executions_errors > 0 ? "var(--danger)" : undefined,
    },
    {
      label: "Taxa de erro",
      value: data ? `${data.error_rate}%` : "—",
      color: data && data.error_rate > 5 ? "var(--danger)" : undefined,
    },
    {
      label: "Mensagens processadas",
      value: data ? fmtNumber(data.messages_processed) : "—",
    },
    {
      label: "Clientes únicos",
      value: data ? fmtNumber(data.unique_customers) : "—",
    },
    {
      label: "Qualificados para humano",
      value: data ? fmtNumber(data.qualified_human) : "—",
    },
    {
      label: "Custo médio por conversa",
      value: data ? fmtUsd(data.avg_cost_per_conversation_usd ?? 0) : "—",
      sub: data ? `${fmtNumber(data.conversations_with_cost ?? 0)} conversas · clique para ver o ranking` : undefined,
      onClick: () => setShowConvCosts(true),
    },
    {
      label: "Tokens de entrada",
      value: data ? fmtNumber(data.tokens_in) : "—",
      tooltip: data
        ? `Input: ${fmtNumber(Math.max(data.tokens_in - (data.tokens_cached ?? 0), 0))} tk\nCache: ${fmtNumber(data.tokens_cached ?? 0)} tk`
        : undefined,
    },
    {
      label: "Tokens de saída",
      value: data ? fmtNumber(data.tokens_out) : "—",
      sub: data ? `Total: ${fmtNumber(data.tokens_total)}` : undefined,
      tooltip: data
        ? `Saída: ${fmtNumber(Math.max(data.tokens_out - (data.tokens_thinking ?? 0), 0))} tk\nThinking: ${fmtNumber(data.tokens_thinking ?? 0)} tk`
        : undefined,
    },
    {
      label: "Tokens de raciocínio",
      value: data ? fmtNumber(data.tokens_thinking) : "—",
      sub: "incluído na saída",
    },
    {
      label: "Tokens em cache",
      value: data ? fmtNumber(data.tokens_cached ?? 0) : "—",
      sub: "incluído na entrada",
    },
  ];

  return (
    <div style={{ padding: "24px" }}>

      {/* Period selector */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginBottom: 24 }}>
        {PERIODS.map(p => {
          const active = !rangeActive && p.value === period;
          return (
            <button
              key={p.value}
              onClick={() => selectPeriod(p.value)}
              style={{
                padding: "5px 14px",
                fontSize: 12,
                borderRadius: 99,
                border: `1px solid ${active ? "var(--brand)" : "var(--line-strong)"}`,
                background: active ? "rgba(195,32,32,0.12)" : "transparent",
                color: active ? "var(--brand)" : "var(--ink-muted)",
                cursor: "pointer",
                transition: "all 0.12s",
              }}
              onMouseOver={e => { if (!active) { e.currentTarget.style.borderColor = "var(--ink-dim)"; e.currentTarget.style.color = "var(--ink-secondary)"; }}}
              onMouseOut={e => { if (!active) { e.currentTarget.style.borderColor = "var(--line-strong)"; e.currentTarget.style.color = "var(--ink-muted)"; }}}
            >
              {p.label}
            </button>
          );
        })}

        {/* Separador + range de datas */}
        <div style={{ width: 1, height: 20, background: "var(--line-strong)", margin: "0 4px" }} />
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 12, color: "var(--ink-muted)" }}>De</span>
          <input
            type="date"
            value={startDate}
            max={endDate || undefined}
            onChange={e => setStartDate(e.target.value)}
            style={{ background: "var(--surface-hover)", border: `1px solid ${rangeActive ? "var(--brand)" : "var(--line-strong)"}`, borderRadius: 6, padding: "5px 8px", fontSize: 12, color: "var(--ink)" }}
          />
          <span style={{ fontSize: 12, color: "var(--ink-muted)" }}>até</span>
          <input
            type="date"
            value={endDate}
            min={startDate || undefined}
            onChange={e => setEndDate(e.target.value)}
            style={{ background: "var(--surface-hover)", border: `1px solid ${rangeActive ? "var(--brand)" : "var(--line-strong)"}`, borderRadius: 6, padding: "5px 8px", fontSize: 12, color: "var(--ink)" }}
          />
          {(startDate || endDate) && (
            <button
              onClick={() => { setStartDate(""); setEndDate(""); }}
              style={{ fontSize: 11, color: "var(--ink-muted)", padding: "3px 6px", borderRadius: 4, background: "none", border: "none", cursor: "pointer" }}
              onMouseOver={e => (e.currentTarget.style.color = "var(--ink)")}
              onMouseOut={e => (e.currentTarget.style.color = "var(--ink-muted)")}
            >
              Limpar
            </button>
          )}
        </div>
      </div>

      {/* Cards */}
      {isLoading ? (
        <div style={{ color: "var(--ink-muted)", fontSize: 13 }}>Carregando...</div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12 }}>
          {cards.map(card => (
            <div
              key={card.label}
              className="metric-card"
              title={card.tooltip}
              onClick={card.onClick}
              style={{
                background: "var(--surface-hover)",
                border: "1px solid var(--line)",
                borderRadius: 10,
                padding: "18px 20px",
                cursor: card.onClick ? "pointer" : card.tooltip ? "help" : undefined,
              }}
            >
              <div style={{ fontSize: 11, color: "var(--ink-muted)", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 10 }}>
                {card.label}
              </div>
              <div className="metric-value" style={{ fontSize: 30, fontWeight: 700, lineHeight: 1, color: card.color }}>
                {card.value}
              </div>
              {card.sub && (
                <div style={{ fontSize: 11, color: "var(--ink-dim)", marginTop: 6 }}>
                  {card.sub}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {!isLoading && data && (
        <div style={{ marginTop: 24, display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 10, padding: "18px 20px", maxWidth: 280 }}>
            <div style={{ fontSize: 11, color: "var(--ink-muted)", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 10 }}>
              Custo do agente
            </div>
            <div style={{ fontSize: 30, fontWeight: 700, color: "var(--brand)", lineHeight: 1 }}>
              {fmtUsd(data.cost_total_usd ?? 0)}
            </div>
          </div>
          <div style={{ background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 10, padding: "18px 20px" }}>
            <CostBar slices={data.cost_breakdown ?? []} />
          </div>
          <PaygSim data={data} />
        </div>
      )}

      {/* Popup: conversas ordenadas por custo (maior → menor) */}
      {showConvCosts && (
        <div
          style={{ position: "fixed", inset: 0, background: "var(--overlay)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: "var(--z-modal)" as any, padding: 24 }}
          {...backdropClose(() => setShowConvCosts(false))}
        >
          <DialogPanel
            onClose={() => setShowConvCosts(false)}
            label="Conversas por custo"
            style={{ background: "var(--surface-card)", border: "1px solid var(--line-bold)", borderRadius: 12, width: "min(480px, 100%)", maxHeight: "85vh", display: "flex", flexDirection: "column" }}
          >
            <div style={{ padding: "18px 22px 12px", borderBottom: "1px solid var(--line)", display: "flex", alignItems: "baseline", gap: 10 }}>
              <span style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)" }}>Conversas por custo</span>
              {convCosts && (
                <span style={{ fontSize: 12, color: "var(--ink-muted)" }}>
                  {fmtNumber(convCosts.length)}{convCosts.length === 50 ? " (top 50)" : ""}
                </span>
              )}
            </div>
            <div style={{ overflowY: "auto", padding: "6px 22px 18px" }}>
              {convCostsLoading && <div style={{ fontSize: 13, color: "var(--ink-muted)", padding: "14px 0" }}>Carregando...</div>}
              {convCosts && convCosts.length === 0 && (
                <div style={{ fontSize: 13, color: "var(--ink-muted)", padding: "14px 0" }}>Nenhuma conversa com custo no período.</div>
              )}
              {convCosts?.map(r => (
                <div
                  key={r.conversation_id}
                  onClick={() => setConvSel(r)}
                  style={{ borderBottom: "1px solid var(--surface-input)", padding: "11px 0", display: "flex", gap: 14, alignItems: "center", cursor: "pointer" }}
                  onMouseOver={e => (e.currentTarget.style.background = "var(--surface-hover)")}
                  onMouseOut={e => (e.currentTarget.style.background = "")}
                >
                  {/* telefone → bsuid → username, o primeiro disponível */}
                  <span style={{ flex: 1, minWidth: 0, fontSize: 13, color: "var(--ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {(r.external_user_id ? fmtPhone(r.external_user_id) : null) || r.bsuid || r.lead_username || "—"}
                  </span>
                  <span style={{ flexShrink: 0, fontSize: 13, fontWeight: 600, color: "var(--brand)" }}>{fmtUsd(r.cost_usd)}</span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: "var(--ink-dim)", flexShrink: 0 }}>
                    <polyline points="9 18 15 12 9 6"/>
                  </svg>
                </div>
              ))}
            </div>
          </DialogPanel>
        </div>
      )}

      {/* Modal da conversa clicada no ranking — mesmo modal da aba Conversas */}
      {convSel && (
        <ConversationModal
          agentId={agent.id}
          convId={convSel.conversation_id}
          phone={convSel.external_user_id}
          bsuid={convSel.bsuid}
          createdAt={convSel.created_at ?? undefined}
          lastMessageAt={convSel.last_message_at ?? undefined}
          variant="whatsapp"
          onClose={() => setConvSel(null)}
        />
      )}
    </div>
  );
}
